import React, { useState, useEffect, useContext, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import _ from 'lodash';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import { GEN_COLORS, RESTYLE_COLORS } from './trims/TrimColors';
import { IconButton } from '../common/IconButton';
import { openGenEditorModal } from './GenDocEditor';
import ModelGenerationsTimelineOverlay from './ModelGenerationsTimelineOverlay';
import useModelsData from './data-caches/useModelsData';
import CountryFlag from '../common/CountryFlag';

const MIN_YEAR = 1980;

const currentYear = new Date().getFullYear();

/**
 * Renders a timeline bar of model generations.
 * Each column represents a year. Overlapping base generations are placed in separate rows.
 * Facelifts (e.g. G1.1, G1.2) are shown as sub-blocks inside their parent generation block.
 *
 * @param {{ context: { modelId?: string }, startHidden?: boolean, onUpdate?: function }} props
 */
const ModelGenerationsTimeline = forwardRef(function ModelGenerationsTimeline({ context, startHidden = false, onUpdate }, ref) {
  const { page } = useContext(LegoAdminPageContext);
  const modelsData = useModelsData();

  const [generations, setGenerations] = useState(null);
  const [hidden, setHidden] = useState(startHidden);
  const [pinned, setPinned] = useState(false);
  const [showFacelifts, setShowFacelifts] = useState(true);
  const [includeSubmodels, setIncludeSubmodels] = useState(true);
  const [multiBaseModelId, setMultiBaseModelId] = useState(false);
  const [overlayVehicles, setOverlayVehicles] = useState(null);
  const tableRef = useRef(null);

  useImperativeHandle(ref, () => ({ setOverlayVehicles }), []);

  const modelId = context?.modelId;

  // The submodels of the model also contribute generations to its timeline (toggleable).
  const submodels = modelId ? modelsData.submodelsOf(modelId) : [];
  const hasSubmodels = submodels.length > 0;

  const fetchGenerations = useCallback(async () => {
    if (!modelId) { setGenerations(null); return; }
    try {
      // Resolve which baseModelIds to query: the model itself, optionally its submodels, and any
      // other base models referenced by this model's declared generations (cross-model gens).
      const subs = includeSubmodels ? modelsData.submodelsOf(modelId) : [];
      const modelGens = modelsData.gens(modelId);
      const baseModelIds = _.uniq([modelId, ...subs].filter(Boolean));

      const gens = await page.service('/services/data/models/gens').find({
        query: {
          $or: [
            { baseModelId: { $in: baseModelIds } },
            { genId: { $in: modelGens.map(g => g.genId) } }
          ]
        },
        $sort: { fromYear: 1 }
      });

      // Add the origin countrie data from modelCountryGens
      for (const g of gens) {
        let match = modelGens.find(mGen => mGen.genId === g.genId);
        if (match) {
          g.countries = match.countries;
        }
      }

      setMultiBaseModelId(new Set(gens.map(g => g.baseModelId)).size > 1);

      setGenerations(gens);
    } catch (err) {
      console.error('Error fetching model generations:', err);
      setGenerations([]);
    }
  }, [modelId, includeSubmodels, modelsData]);

  useEffect(() => { fetchGenerations(); }, [fetchGenerations]);

  const onGenClick = (displayGen) => {
    const original = generations.find(g => g._id === displayGen._id) || displayGen;
    openGenEditorModal(page, original, {
      onSaved: () => {
        fetchGenerations();
        onUpdate && onUpdate();
      }
    });
  };

  if (!modelId || !generations || !generations.length) return null;

  // The precise set of genIds that are actually possible (declared) for this model. It's used to
  // flag generations that exist in the catalog (the `find` above) but aren't declared anywhere —
  // those are shown greyed out (light-secondary).
  //
  // Source of truth is the per-country generation declarations (modelCountryGen): we take the union
  // of genIds declared across ALL countries. Special case: if the model has NO 'global' country
  // entry, the per-country declarations aren't meant to be exhaustive, so every catalog gen with
  // baseModelId === modelId is ALSO considered possible (i.e. none of the model's own gets flagged).
  const declaredGenIds = new Set(modelsData.gens(modelId).map(g => g.genId));
  const hasGlobalCountryGens = modelsData.gens(modelId, 'global').length > 0;

  const possibleGenIds = new Set(declaredGenIds);
  if (!hasGlobalCountryGens) {
    for (const g of generations) {
      if (g.baseModelId === modelId) possibleGenIds.add(g.genId);
    }
  }

  // Filter out generations entirely before MIN_YEAR; clamp fromYear for display
  const validGens = generations
    .filter(g => g.fromYear && (!g.toYear || g.toYear >= MIN_YEAR))
    .map(g => ({
      ...g,
      fromYear: Math.max(g.fromYear, MIN_YEAR),
      toYear: g.toYear || currentYear,
      // Belongs to this model but isn't among its possible (declared) gens → render as "not there".
      notInCountryGens: g.baseModelId === modelId && !possibleGenIds.has(g.genId),
    }));

  if (!validGens.length) return null;

  // Separate base generations from facelifts (gen contains a dot, e.g. "1.1")
  const baseGens = [];
  const faceliftsByBaseGen = {};

  for (const gen of validGens) {
    const genStr = (gen.gen || '0').toString();
    const isSubgen = genStr.includes('.');

    if (isSubgen) {
      const baseGenId = gen.genId.split('.')[0];
      if (!faceliftsByBaseGen[baseGenId]) faceliftsByBaseGen[baseGenId] = [];
      faceliftsByBaseGen[baseGenId].push(gen);
    } else {
      baseGens.push(gen);
    }
  }

  // Orphan facelifts (no matching base gen) become standalone entries
  const baseGenIds = new Set(baseGens.map(g => g.genId));
  for (const [baseGenId, facelifts] of Object.entries(faceliftsByBaseGen)) {
    if (!baseGenIds.has(baseGenId)) {
      baseGens.push(...facelifts);
      delete faceliftsByBaseGen[baseGenId];
    }
  }

  if (!baseGens.length) return null;

  // Compute year range including facelifts
  const allEntries = [...baseGens, ..._.flatten(Object.values(faceliftsByBaseGen))];
  const minYear = Math.min(1995, Math.max(MIN_YEAR, _.min(allEntries.map(g => g.fromYear))));
  const maxYear = currentYear;
  const years = _.range(minYear, maxYear + 1);

  // Assign base gens to rows using greedy non-overlapping placement
  const sortedGens = _.sortBy(baseGens, 'fromYear', 'gen');
  const rows = [];

  for (const gen of sortedGens) {
    let placed = false;
    for (const row of rows) {
      const overlaps = row.some(existing =>
        gen.fromYear <= existing.toYear && gen.toYear >= existing.fromYear
      );
      if (!overlaps) {
        row.push(gen);
        placed = true;
        break;
      }
    }
    if (!placed) {
      rows.push([gen]);
    }
  }

  const hasFacelifts = Object.keys(faceliftsByBaseGen).length > 0;

  return (
    <div className={`border-top px-1 position-relative ${hidden ? '' : 'bg-patito'} ${pinned ? 'position-sticky' : ''}`}
      style={{
        overflowX: hidden ? undefined : 'auto',
        top: pinned ? '54px' : undefined,
        minHeight: overlayVehicles ? '60px' : '26px',
        marginBottom: hidden ? '-26px' : undefined,
        zIndex: pinned ? 1 : undefined,
      }}>

      {!hidden && (
        <div className='position-relative'>
          <table ref={tableRef} className='w-100' style={{
            borderCollapse: 'separate', borderSpacing: '1px 2px', tableLayout: 'fixed',
            minHeight: overlayVehicles ? '60px' : undefined,
          }}>
            <thead>
              <tr>
                {years.map(year => (
                  <th key={year} className='text-center text-muted font-weight-normal'
                    style={{ padding: '1px 2px', fontSize: '9px', minWidth: '32px', borderRight: 'solid 1px #ddd' }}>
                    {year}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <GenerationRow key={rowIndex} rowGens={row} years={years}
                  showFullName={multiBaseModelId}
                  faceliftsByBaseGen={showFacelifts ? faceliftsByBaseGen : {}}
                  onGenClick={onGenClick} />
              ))}
            </tbody>
          </table>
          {overlayVehicles?.length > 0 && (
            <ModelGenerationsTimelineOverlay vehicles={overlayVehicles} years={years} tableRef={tableRef} />
          )}
        </div>
      )}

      <span className='position-absolute d-flex align-items-center' style={{ bottom: '1px', right: '2px', zIndex: 2, gap: '1px' }}>
        <IconButton icon={hidden ? 'visibility' : 'visibility_off'} small
          level='secondary'
          title={hidden ? 'Show generations bar' : 'Hide generations bar'}
          onClick={() => setHidden(h => !h)}>{hidden ? 'Show gens' : ''}</IconButton>

        {hasFacelifts && !hidden ? <IconButton icon={showFacelifts ? 'layers' : 'layers_clear'} small
          level={showFacelifts ? 'info' : 'secondary'}
          title={showFacelifts ? 'Hide facelifts' : 'Show facelifts'}
          onClick={() => setShowFacelifts(f => !f)} />
          : null}

        {hasSubmodels && !hidden ? <IconButton icon={includeSubmodels ? 'account_tree' : 'lan'} small
          level={includeSubmodels ? 'info' : 'secondary'}
          title={includeSubmodels ? 'Hide submodels\' generations' : 'Show submodels\' generations'}
          onClick={() => setIncludeSubmodels(s => !s)} />
          : null}

        {!hidden ? <IconButton icon={'push_pin'} small
          level={pinned ? 'primary' : 'secondary'}
          title='Show pinned to top'
          onClick={() => setPinned(f => !f)}>{pinned ? 'Unpin' : ''}</IconButton> : null}
      </span>
    </div>
  );
});

export default ModelGenerationsTimeline;

function GenerationRow({ rowGens, years, showFullName, faceliftsByBaseGen, onGenClick }) {
  const sorted = _.sortBy(rowGens, 'fromYear');
  const cells = [];
  let yearIndex = 0;

  for (const gen of sorted) {
    const startIdx = years.indexOf(gen.fromYear);
    const endIdx = years.indexOf(gen.toYear);

    if (startIdx < 0 || endIdx < 0) continue;

    // Empty cells before this generation
    if (startIdx > yearIndex) {
      cells.push(<td key={`gap-${yearIndex}`} colSpan={startIdx - yearIndex} />);
    }

    const span = endIdx - startIdx + 1;
    const genStr = (gen.gen || '0').toString();
    const genBase = genStr.split('.')[0].replace(/[a-f]$/ig, '');
    const facelifts = faceliftsByBaseGen[gen.genId] || [];

    let colorClass = GEN_COLORS[genBase] || 'light-secondary';
    if (gen.remappedToGenId) {
      colorClass = 'light-secondary';
    }
    if (gen.notInCountryGens) {
      // Exists in the gens catalog but not declared as possible for this model in any country.
      colorClass = 'light-secondary translucent';
    }

    cells.push(
      <td
        key={gen.genId || gen._id}
        colSpan={span}
        className={`bg-${colorClass} text-center rounded border align-top`}
        style={{ fontSize: '11px', fontWeight: 500, lineHeight: '1.3', cursor: 'pointer' }}
        title={`${gen.name || ''} (Gen ${gen.gen}) ${gen.fromYear}–${gen.toYear}${gen.codenames?.length ? '\n' + gen.codenames.join(', ') : ''}${gen.notInCountryGens ? '\n⚠ Not declared as possible in any country' : ''}`}
        onClick={() => onGenClick(gen)}
      >
        <div className='text-truncate'>
          {gen.countries?.size > 0 && <span className='mr-1 zoom-75 align-middle'>{[...gen.countries].map(c => <CountryFlag key={c} countryCode={c} small />)}</span>}
          <strong>
            <span className={gen.remappedToGenId ? 'text-strike' : ''}>{showFullName ? `${gen.baseModelId}@G${gen.gen}` : `G${gen.gen}`}</span>
            {gen.remappedToGenId ? <span className='ml-1 text-primary' title={`Remapped to ${gen.remappedToGenId}`}>⤳ {gen.remappedToGenId}</span> : null}
          </strong>
          {gen.name ? (
            <span className='ml-1' style={{ fontSize: '10px', opacity: 0.85 }}>{gen.name}</span>
          ) : null}
          {gen.codenames && gen.codenames.length > 0 && (
            <span className='ml-2 text-muted' style={{ fontSize: '9px', opacity: 0.75 }}>
              [{gen.codenames.join(', ')}]
            </span>
          )}
          {gen.nicknames && gen.nicknames.length > 0 && (
            <span className='ml-2 text-info' style={{ fontSize: '9px', fontStyle: 'italic', opacity: 0.7 }}>
              “{gen.nicknames.join(' / ')}”
            </span>
          )}
        </div>

        {facelifts.length > 0 && (
          <FaceliftSubBlocks facelifts={facelifts} parentFromYear={gen.fromYear} parentToYear={gen.toYear} onGenClick={onGenClick} />
        )}

        {gen.otherModelRelations?.isIdenticalCar?.length ? <div className='mt-1 text-center zoom-90'>
          {gen.otherModelRelations.isIdenticalCar.map(r => <span className='badge badge-violeta mr-1' key={r.with}>{r.with}</span>)}
        </div> : null}
      </td>
    );

    yearIndex = endIdx + 1;
  }

  // Fill remaining empty space
  if (yearIndex < years.length) {
    cells.push(<td key={'gap-end'} colSpan={years.length - yearIndex} />);
  }

  return <tr>{cells}</tr>;
}

function FaceliftSubBlocks({ facelifts, parentFromYear, parentToYear, onGenClick }) {
  const parentSpan = parentToYear - parentFromYear;
  if (parentSpan <= 0) return null;

  const sorted = _.sortBy(facelifts, 'fromYear');

  return (
    <div className='position-relative mt-1' style={{ height: `${sorted.length * 16}px` }}>
      {sorted.map((fl, i) => {
        const clampedFrom = Math.max(fl.fromYear, parentFromYear);
        const clampedTo = Math.min(fl.toYear, parentToYear);
        const leftPct = ((clampedFrom - parentFromYear) / parentSpan) * 100;
        const widthPct = ((clampedTo - clampedFrom) / parentSpan) * 100;

        const restyleN = fl.gen.toString().split('.')[1] || '1';
        // Facelift exists in the catalog but isn't declared as possible for this model → grey it out.
        const borderColor = fl.notInCountryGens ? 'secondary' : (RESTYLE_COLORS[restyleN] || 'info');

        return (
          <div
            key={fl.genId || fl._id}
            className={`position-absolute text-center text-truncate border border-2 border-${borderColor} rounded`}
            style={{
              left: `${leftPct}%`,
              width: `${Math.max(widthPct, 8)}%`,
              top: `${i * 16}px`,
              height: '14px',
              fontSize: '9px',
              lineHeight: '12px',
              backgroundColor: 'rgba(255,255,255,0.55)',
              cursor: 'pointer',
            }}
            title={`${fl.name || ''} (Gen ${fl.gen}) ${fl.fromYear}–${fl.toYear}${fl.codenames?.length ? '\n' + fl.codenames.join(', ') : ''}${fl.notInCountryGens ? '\n⚠ Not declared as possible in any country' : ''}`}
            onClick={(e) => { e.stopPropagation(); onGenClick(fl); }}
          >
            <strong>{fl.gen}</strong>
            {fl.name ? <span className='ml-1' style={{ opacity: 0.8 }}>{fl.name}</span> : null}
          </div>
        );
      })}
    </div>
  );
}
